import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, Loader2 } from "lucide-react";
import { SETTINGS_SECTIONS, sectionLabel } from "@/components/settings/sections";
import { SectionHome } from "@/components/settings/SectionHome";
import { SectionAccount } from "@/components/settings/SectionAccount";
import { SectionSecurity } from "@/components/settings/SectionSecurity";
import { SectionSessions } from "@/components/settings/SectionSessions";
import { SectionPreferences } from "@/components/settings/SectionPreferences";
import { SectionBilling } from "@/components/settings/SectionBilling";
import { SectionData } from "@/components/settings/SectionData";
import { SectionIntegrations, SectionImportExport } from "@/components/settings/SectionIntegrations";
import { SectionTeam } from "@/components/settings/SectionTeam";
import { cn } from "@/lib/utils";
import api from "@/lib/api";
import { useLang, translateFr as tfr } from "@/i18n";

const COMPONENTS = {
  account: SectionAccount,
  team: SectionTeam,
  security: SectionSecurity,
  sessions: SectionSessions,
  preferences: SectionPreferences,
  billing: SectionBilling,
  data: SectionData,
  danger: SectionData,
  integrations: SectionIntegrations,
  "import-export": SectionImportExport,
};

export default function Settings() {
  const navigate = useNavigate();
  const { t, tf } = useLang();
  const { section } = useParams();
  const active = section && COMPONENTS[section] ? section : null;
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/account/overview");
      setOverview(r.data);
    } catch (e) { console.error("overview", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const Active = active ? COMPONENTS[active] : null;

  const Nav = ({ mobile = false }) => (
    <nav aria-label={tfr("Navigation des paramètres")} className={mobile ? "space-y-1" : "space-y-0.5"} data-testid={mobile ? "settings-nav-mobile" : "settings-nav"}>
      {!mobile && <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground px-3 pb-2">{t("nav.settings")}</p>}
      {SETTINGS_SECTIONS.map((s) => {
        const Icon = s.icon;
        const isActive = active === s.id;
        return (
          <button key={s.id} disabled={s.soon}
            onClick={() => navigate(`/settings/${s.id}`)}
            data-testid={`settings-nav-${s.id}`}
            className={cn(
              "w-full flex items-center gap-2.5 rounded-lg px-3 text-sm transition-colors text-left",
              mobile ? "py-3 border border-white/[0.06] bg-surface rounded-xl" : "py-2",
              s.soon ? "opacity-40 cursor-not-allowed"
                : isActive ? "bg-white/[0.06] text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/[0.03]",
              s.danger && !s.soon && "text-red-400/80 hover:text-red-400",
            )}>
            <Icon className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate">{sectionLabel(s)}</span>
            {s.soon && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{tf("Bientôt")}</span>}
            {mobile && !s.soon && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </button>
        );
      })}
    </nav>
  );

  if (loading && !overview) {
    return <div className="flex justify-center py-24" data-testid="settings-loading"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 sm:px-6 pt-6 pb-16" data-testid="settings-page">
      <header className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.02em]">{t("nav.settings")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("settings.subtitle")}</p>
      </header>

      {/* Single content tree — only the nav chrome varies by breakpoint */}
      <div className="md:grid md:grid-cols-[220px_1fr] md:gap-8">
        <aside className="hidden md:block sticky top-20 self-start"><Nav /></aside>
        <AnimatePresence mode="wait">
          <motion.div key={active || "home"} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }} className="min-w-0">
            {Active ? (
              <Active overview={overview} reload={reload} />
            ) : (
              <div className="space-y-5">
                <SectionHome overview={overview} />
                <div className="md:hidden"><Nav mobile /></div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
